import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { CatalogOperationsFacade } from '../../../Facades/catalog-operations.facade';
import { ApiService } from '../../Shared/Services/api.service';
import { NavigationLockService } from '../../Shared/Services/navigation-lock.service';
import { GroupDemandsService } from '../Services/group-demands.service';
import { DemandCreatedToastService } from '../Services/demand-created-toast.service';
import { endpoints } from '../../../../environments/endpoints';
import {
  GroupDetail,
  GroupDetailResponse,
} from '../../../Interfaces/api/groups/detail.interface';
import { GroupDemand } from '../../../Models/avyna/group-demand.model';
import { UserDemandEntry } from '../Interfaces/user-demands-list.interface';

@Injectable()
export class GroupDetailFacade {
  private readonly _api = inject(ApiService);
  private readonly _demandsService = inject(GroupDemandsService);
  private readonly _catalogOperations = inject(CatalogOperationsFacade);
  private readonly _createdToast = inject(DemandCreatedToastService);
  private readonly _navLock = inject(NavigationLockService);

  private readonly _groupId = signal<string>('');
  readonly groupId = computed(() => this._groupId());

  private readonly _submitting = signal<boolean>(false);
  readonly submitting = computed<boolean>(() => this._submitting());

  readonly group = httpResource<GroupDetailResponse>(() => {
    const id = this._groupId();
    if (!id) return undefined;
    return this._api.get<GroupDetailResponse>(endpoints.api.catalog.groups.show(id), {
      skipLoading: true,
    });
  });

  readonly groupData = computed<GroupDetail | undefined>(() =>
    this.group.hasValue() ? this.group.value().data : undefined,
  );

  readonly userDemand = computed<GroupDemand | undefined>(() => {
    const raw = this.groupData()?.current_user_demand;
    return raw ? new GroupDemand(raw) : undefined;
  });

  readonly requiresApproval = computed<boolean>(() => {
    const group = this.groupData();
    return !!group && (group.group_approvals?.length ?? 0) > 0;
  });

  readonly loading = computed<boolean>(() => this.group.isLoading() || this._submitting());

  readonly bottomEntries = computed<UserDemandEntry[]>(() => {
    const demand = this.userDemand();
    if (!demand || !demand.id) return [];
    return [
      {
        demandId: demand.id,
        reference: demand.reference,
        status: demand.status ?? '',
        createdAt: demand.created_at ?? '',
      },
    ];
  });

  setGroupId(id: string): void {
    this._groupId.set(id);
  }

  reset(): void {
    this._groupId.set('');
  }

  async requestToJoin(): Promise<void> {
    const id = this._groupId();
    if (!id || this._submitting()) return;
    this._submitting.set(true);
    this._navLock.lock();
    try {
      const response = await firstValueFrom(this._demandsService.store(id, {}));
      this.group.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      // The error interceptor already shows the API message as a toast.
    } finally {
      this._navLock.unlock();
      this._submitting.set(false);
    }
  }

  async requestToJoinForDevice(intuneDeviceId: string): Promise<void> {
    const id = this._groupId();
    if (!id) return;
    this._navLock.lock();
    try {
      const response = await firstValueFrom(
        this._demandsService.store(id, { device_intune_id: intuneDeviceId }),
      );
      this.group.reload();
      this._catalogOperations.reload();
      this._createdToast.announce(response);
    } catch {
      // The error interceptor already shows the API message as a toast.
    } finally {
      this._navLock.unlock();
    }
  }
}
